import React, { useState } from "react";
import { Card } from "react-bootstrap";
import PropTypes from "prop-types";
import { useStateMachine } from "little-state-machine";

import { getFirstChar } from "helpers/commonHelpers";
import testIds from "constants/testIds";
import usePermissions from "hooks/usePermissions";
import { ReactComponent as LockIcon } from "assets/images/icons/Lock.svg";
import { ReactComponent as LockOpenIcon } from "assets/images/icons/LockOpen.svg";
import { ReactComponent as ResetIcon } from "assets/images/icons/Reset.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/Add.svg";
import { ReactComponent as MenuIcon } from "assets/images/icons/Menu.svg";
import ResetDashboardDialog from "../../ResetDashboardDialog";

import "./UserWidget.scss";

type UserWidgetProps = {
  isLayoutLocked: boolean;
  setIsLayoutLocked: React.Dispatch<React.SetStateAction<boolean>>;
  openWidgetDialog: () => void;
  firstName: string;
  lastName: string;
};

function UserWidget(props: UserWidgetProps) {
  const { isLayoutLocked, setIsLayoutLocked, openWidgetDialog, firstName, lastName } = props;
  const { permissions } = usePermissions();
  const companyName = JSON.parse(permissions || "{}")?.company_name;
  const { state }: any = useStateMachine();

  const [showResetDashboardDialog, setShowResetDashboardDialog] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleResetLayoutClick = () => {
    setShowResetDashboardDialog(true);
  };

  return (
    <>
      <Card.Body className="user-widget">
        <div className="d-flex align-items-center h-100">
          <div className="d-flex align-items-center">
            <div className="avatar-container">
              {getFirstChar(firstName)}
              {getFirstChar(lastName?.split?.(" ")?.length > 1 ? lastName.split(" ")[1] : lastName)}
            </div>
            {isLayoutLocked && (
              <div className="d-flex flex-column ml-2">
                <h5>
                  {firstName} {getFirstChar(lastName?.split?.(" ")?.length > 1 ? lastName.split(" ")[1] : lastName)}.
                </h5>
                <p className="font-b3 text-secondary mt-1">{state.companyName || companyName}</p>
              </div>
            )}
          </div>
          <div className="ml-auto icons" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            {!isLayoutLocked && (
              <>
                <ResetIcon
                  style={{ transform: "scale(1.2)" }}
                  className="mr-3 cursor-pointer"
                  onClick={handleResetLayoutClick}
                />
                <AddIcon
                  style={{ transform: "scale(1.2)" }}
                  className="mr-3 cursor-pointer"
                  onClick={openWidgetDialog}
                  data-testid={testIds.OPEN_WIDGET_DIALOG_BUTTON}
                />
              </>
            )}

            {isLayoutLocked && isHovered && (
              <LockIcon
                className="cursor-pointer"
                style={{ transform: "scale(1.2)" }}
                onClick={() => {
                  setIsLayoutLocked(false);
                }}
                data-testid={testIds.LAYOUT_LOCK_BUTTON}
              />
            )}
            {isLayoutLocked && !isHovered && <MenuIcon className="cursor-pointer" style={{ transform: "scale(1.2)" }} />}
            {!isLayoutLocked && (
              <LockOpenIcon
                style={{ transform: "scale(1.2)" }}
                className="cursor-pointer"
                onClick={() => {
                  setIsHovered(false);
                  setIsLayoutLocked(true);
                }}
                data-testid={testIds.LAYOUT_LOCK_BUTTON}
              />
            )}
          </div>
        </div>
      </Card.Body>
      <ResetDashboardDialog
        isShowing={showResetDashboardDialog}
        close={() => {
          setShowResetDashboardDialog(false);
        }}
      />
    </>
  );
}

export default UserWidget;

UserWidget.propTypes = {
  isLayoutLocked: PropTypes.bool,
  setIsLayoutLocked: PropTypes.func.isRequired,
  openWidgetDialog: PropTypes.func.isRequired,
};

UserWidget.defaultProps = {
  isLayoutLocked: false,
};
